/**
 * StudioContentsPane — the Studio's layer and object tree.
 *
 * Generated components retain CAD-style selection, pin and fit flags. Optional
 * host data expands the tree to parcels, references, constraints, acoustic
 * barriers and analysis overlays so imported/context geometry is discoverable
 * rather than silently living outside the workstation.
 */
import React, { useEffect, useRef } from 'react';
import {
  AlertTriangle,
  Eye,
  EyeOff,
  Focus,
  Layers,
  Lock,
  LockOpen,
  MapPinned,
  Pin,
  ShieldAlert,
  Unlink,
  Volume2,
} from 'lucide-react';

import type { GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import type {
  GenerationTestFitComponentKind,
  GenerationTestFitDimensionBasis,
} from '../../../../lib/generationImpact/generationTestFit';
import type {
  GenerationAcousticBarrier,
  GenerationImportedLayerAssignment,
  GenerationLayoutExclusionZone,
} from '../../../../types/generationImpact';
import type { SiteLayer } from '../../../../types/site';
import type { SiteBoundaryParcel } from '../../../../types/siteBoundary';
import { CATEGORY_LABELS, CATEGORY_ORDER } from '../../../../config/siteLayerColors';
import type { SiteLayerCategory } from '../../../../types/site';
import { COMPONENT_COLORS, componentFlagged } from './StudioCanvas';
import {
  dimensionProvenance,
  exclusionKindLabel,
  groupLayersByCategory,
  type DimensionProvenanceTone,
} from './studioLayers';

const GROUP_LABELS: Record<GenerationTestFitComponentKind, string> = {
  'generation-block': 'Generation blocks',
  cooling: 'Cooling',
  switchyard: 'Switchyard',
  'ccs-capture': 'CCS capture',
  'ccs-compression': 'CCS compression',
  'natural-gas-route': 'Gas route',
  'co2-export-route': 'CO₂ route',
  'construction-laydown': 'Laydown',
  'access-road': 'Access roads',
  road: 'Roads',
  parking: 'Parking',
  building: 'Buildings',
  'data-center': 'Data centers',
  generator: 'Generators',
  custom: 'Custom',
};

const GROUP_ORDER: readonly GenerationTestFitComponentKind[] = [
  'generation-block', 'cooling', 'switchyard', 'ccs-capture', 'ccs-compression',
  'data-center', 'building', 'generator', 'parking', 'access-road', 'road',
  'natural-gas-route', 'co2-export-route', 'construction-laydown', 'custom',
];

/** Studio-category label per imported-source role — distinct from the Site Map
 *  evidence taxonomy, and explicit about capacity effect. */
const STUDIO_ROLE_LABELS: Record<GenerationImportedLayerAssignment['role'], string> = {
  exclusion: 'No-build exclusion',
  barrier: 'Acoustic barrier',
  custom: 'Custom object · geometry only',
  equipment: 'Required equipment · modeled',
};

export type StudioContentsTarget =
  | { kind: 'component'; id: string }
  | { kind: 'parcel'; id: string }
  | { kind: 'site-layer'; id: string }
  | { kind: 'exclusion'; id: string }
  | { kind: 'barrier'; id: string }
  | { kind: 'frontline'; id: 'frontline' }
  | { kind: 'noise'; id: 'noise' };

export interface StudioContentsPaneProps {
  testFit: GenerationParcelTestFitResult;
  selectedId: string | null;
  /** Full multi-selection set for highlighting (defaults to [selectedId]). */
  selectedIds?: readonly string[];
  onSelect: (id: string | null, additive?: boolean) => void;
  /** Persisted imported-source → Studio-role assignments (schema-v2 layout). */
  importedAssignments?: readonly GenerationImportedLayerAssignment[];
  /** Remove a source's derived geometry + record; equipment returns to packer. */
  onUnassignSource?: (assignmentId: string) => void;
  parcels?: readonly SiteBoundaryParcel[];
  siteLayers?: readonly SiteLayer[];
  acousticBarriers?: readonly GenerationAcousticBarrier[];
  frontlineSet?: boolean;
  noiseAvailable?: boolean;
  noiseVisible?: boolean;
  onToggleNoise?: () => void;
  onToggleSiteLayer?: (layerId: string, visible: boolean) => void;
  siteLayerOpacity?: Readonly<Record<string, number>>;
  onSetSiteLayerOpacity?: (layerId: string, opacity: number) => void;
  /** Category keys (enum or custom) whose whole group is hidden. */
  hiddenCategories?: ReadonlySet<string>;
  /** Toggle a whole category group's visibility. */
  onToggleCategory?: (categoryKey: string) => void;
  hiddenComponentIds?: ReadonlySet<string>;
  lockedObjectIds?: ReadonlySet<string>;
  onToggleComponentVisibility?: (componentId: string) => void;
  onToggleObjectLock?: (objectId: string) => void;
  onZoomTo?: (target: StudioContentsTarget) => void;
  /** Bulk site-layer operations (act on the multi-selection). */
  onHideSiteLayers?: (ids: readonly string[]) => void;
  onCopySiteLayers?: (ids: readonly string[]) => void;
  onDeleteSiteLayers?: (ids: readonly string[]) => void;
  /** Merge same-geometry-type site layers into one. */
  onMergeSiteLayers?: (ids: readonly string[]) => void;
  /** Move (recategorize) several layers at once. */
  onMoveSiteLayers?: (ids: readonly string[], category: SiteLayerCategory) => void;
}

const LayerSection: React.FC<{
  label: string;
  count: number;
  issueCount?: number;
  testId: string;
  children: React.ReactNode;
}> = ({ label, count, issueCount = 0, testId, children }) => (
  <details open className="group border-b border-border/70 last:border-b-0" data-testid={testId}>
    <summary className="flex min-h-8 cursor-pointer list-none items-center gap-1.5 px-2 text-[9px] font-bold uppercase tracking-[0.16em] text-text-muted outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary">
      <span className="text-text-muted transition-transform group-open:rotate-90" aria-hidden>›</span>
      <span>{label}</span>
      <span className="ml-auto rounded-sm bg-background px-1.5 py-0.5 font-normal tabular-nums text-text-muted">
        {count}
      </span>
      {issueCount > 0 && (
        <span className="rounded-sm bg-warning/15 px-1.5 py-0.5 font-normal tabular-nums text-warning">
          {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
        </span>
      )}
    </summary>
    <div className="pb-1">{children}</div>
  </details>
);

const ZoomButton: React.FC<{
  label: string;
  onClick?: () => void;
  testId?: string;
}> = ({ label, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={!onClick}
    className="grid h-7 w-7 shrink-0 place-items-center text-text-muted transition-colors hover:bg-background hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:hidden"
    aria-label={`Zoom to ${label}`}
    title={`Zoom to ${label}`}
    {...(testId ? { 'data-testid': testId } : {})}
  >
    <Focus className="h-3.5 w-3.5" aria-hidden />
  </button>
);

const VisibilityButton: React.FC<{
  label: string;
  visible: boolean;
  onToggle?: () => void;
}> = ({ label, visible, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={!onToggle}
    className="grid h-7 w-7 shrink-0 place-items-center text-text-muted transition-colors hover:bg-background hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default disabled:opacity-60"
    aria-label={`${visible ? 'Hide' : 'Show'} ${label}`}
    aria-pressed={visible}
    title={`${visible ? 'Hide' : 'Show'} ${label}`}
  >
    {visible
      ? <Eye className="h-3.5 w-3.5" aria-hidden />
      : <EyeOff className="h-3.5 w-3.5" aria-hidden />}
  </button>
);

const LockButton: React.FC<{
  label: string;
  locked: boolean;
  onToggle?: () => void;
}> = ({ label, locked, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    disabled={!onToggle}
    className="grid h-7 w-7 shrink-0 place-items-center text-text-muted transition-colors hover:bg-background hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default disabled:opacity-60"
    aria-label={`${locked ? 'Unlock' : 'Lock'} ${label}`}
    aria-pressed={locked}
    title={`${locked ? 'Unlock' : 'Lock'} ${label}`}
  >
    {locked
      ? <Lock className="h-3.5 w-3.5" aria-hidden />
      : <LockOpen className="h-3.5 w-3.5" aria-hidden />}
  </button>
);

const PROVENANCE_TONE_CLASS: Record<DimensionProvenanceTone, string> = {
  oem: 'border-success/50 text-success',
  surrogate: 'border-warning/50 text-warning',
  reserved: 'border-border text-text-secondary',
  user: 'border-border text-text-muted',
};

/** Tiny chip advertising where a generated component's dimensions came from —
 *  so an OEM-installed footprint reads differently from a planning surrogate. */
const ProvenanceChip: React.FC<{
  basis: GenerationTestFitDimensionBasis;
  testId?: string;
}> = ({ basis, testId }) => {
  const prov = dimensionProvenance(basis);
  return (
    <span
      className={`shrink-0 border px-1 text-[10px] font-bold uppercase leading-tight tracking-wider ${PROVENANCE_TONE_CLASS[prov.tone]} rounded-sm`}
      title={prov.title}
      aria-label={`Dimension basis: ${prov.title}`}
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {prov.badge}
    </span>
  );
};

const ContextRow: React.FC<{
  label: string;
  detail?: string;
  color?: string;
  icon?: React.ReactNode;
  current?: boolean;
  selected?: boolean;
  onSelect?: (additive: boolean) => void;
  visible?: boolean;
  onToggleVisibility?: () => void;
  locked?: boolean;
  onToggleLock?: () => void;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
  onZoom?: () => void;
  /** Stable layer id used to scroll the row into view on external selection. */
  rowId?: string;
  testId: string;
}> = ({
  label,
  detail,
  color,
  icon,
  current = false,
  selected = false,
  onSelect,
  visible,
  onToggleVisibility,
  locked,
  onToggleLock,
  opacity,
  onOpacityChange,
  onZoom,
  rowId,
  testId,
}) => (
  <div
    className={`flex min-h-8 items-center gap-1 border-l-2 px-1 ${
      selected
        ? 'border-l-primary bg-primary/10'
        : current
          ? 'border-l-amber-500 bg-amber-500/10'
          : 'border-l-transparent hover:bg-background/70'
    }`}
    data-testid={testId}
    {...(rowId ? { 'data-layer-id': rowId } : {})}
    {...(current ? { 'aria-current': 'true' as const } : {})}
  >
    {visible != null
      ? (
          <VisibilityButton
            label={label}
            visible={visible}
            {...(onToggleVisibility ? { onToggle: onToggleVisibility } : {})}
          />
        )
      : (
          <span className="grid h-7 w-7 shrink-0 place-items-center text-text-muted">
            {icon ?? (
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: color ?? '#64748b' }}
                aria-hidden
              />
            )}
          </span>
        )}
    {onSelect
      ? (
          <button
            type="button"
            onClick={(event) => onSelect(event.shiftKey || event.metaKey || event.ctrlKey)}
            aria-pressed={selected}
            className="flex min-w-0 flex-1 flex-col items-start px-1 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            data-testid={`${testId}-select`}
          >
            <span className="block max-w-full truncate text-[10px] text-text-primary" title={label}>{label}</span>
            {detail && <span className="block max-w-full truncate text-[9px] text-text-muted">{detail}</span>}
          </button>
        )
      : (
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[10px] text-text-primary" title={label}>{label}</span>
            {detail && <span className="block truncate text-[9px] text-text-muted">{detail}</span>}
          </span>
        )}
    {current && (
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-amber-400">
        Target
      </span>
    )}
    {opacity != null && onOpacityChange && (
      <input
        type="range"
        min={0.1}
        max={1}
        step={0.1}
        value={opacity}
        onChange={event => onOpacityChange(Number(event.target.value))}
        aria-label={`${label} opacity`}
        title={`${Math.round(opacity * 100)}% opacity`}
        className="w-14 accent-primary"
      />
    )}
    {locked != null && (
      <LockButton
        label={label}
        locked={locked}
        {...(onToggleLock ? { onToggle: onToggleLock } : {})}
      />
    )}
    <ZoomButton
      label={label}
      {...(onZoom ? { onClick: onZoom } : {})}
    />
  </div>
);

export const StudioContentsPane: React.FC<StudioContentsPaneProps> = ({
  testFit,
  selectedId,
  selectedIds,
  onSelect,
  importedAssignments = [],
  onUnassignSource,
  parcels = [],
  siteLayers = [],
  acousticBarriers = [],
  frontlineSet = false,
  noiseAvailable = false,
  noiseVisible = false,
  onToggleNoise,
  onToggleSiteLayer,
  hiddenCategories = new Set<string>(),
  onToggleCategory,
  siteLayerOpacity = {},
  onSetSiteLayerOpacity,
  hiddenComponentIds = new Set<string>(),
  lockedObjectIds = new Set<string>(),
  onToggleComponentVisibility,
  onToggleObjectLock,
  onZoomTo,
  onHideSiteLayers,
  onCopySiteLayers,
  onDeleteSiteLayers,
  onMergeSiteLayers,
  onMoveSiteLayers,
}) => {
  // Highlight the whole multi-selection; fall back to the primary id.
  const selectedIdSet = new Set(selectedIds ?? (selectedId != null ? [selectedId] : []));
  // Site-layer ids within the current multi-selection drive the bulk-action bar.
  const selectedSiteLayers = (siteLayers ?? []).filter(layer => selectedIdSet.has(layer.id));
  const selectedSiteLayerIds = selectedSiteLayers.map(layer => layer.id);
  const canMergeSelection = selectedSiteLayers.length >= 2
    && selectedSiteLayers.every(layer => layer.geometry.type === selectedSiteLayers[0]!.geometry.type);
  // Bidirectional selection: when the primary selection changes (e.g. a canvas
  // click), scroll its tree row into view so tree ↔ canvas stay in sync. Rows
  // stamp `data-layer-id`; `block:'nearest'` no-ops when the row is already
  // visible (so selecting from the tree itself doesn't jump the scroll).
  const paneRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selectedId == null) return;
    // Match by attribute value rather than a CSS selector so arbitrary ids
    // (and environments without `CSS.escape`, e.g. jsdom) are handled safely.
    const rows = paneRef.current?.querySelectorAll('[data-layer-id]');
    for (const row of rows ?? []) {
      if (row.getAttribute('data-layer-id') === selectedId) {
        if (typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
        break;
      }
    }
  }, [selectedId]);
  const componentGroups = GROUP_ORDER
    .map(kind => ({
      kind,
      components: testFit.components.filter(component => component.rectangle.kind === kind),
    }))
    .filter(group => group.components.length > 0);
  const referenceLayers = siteLayers.filter(layer => !layer.category.startsWith('constraint.'));
  const constraintLayers = siteLayers.filter(layer => layer.category.startsWith('constraint.'));
  const exclusions = testFit.layout.exclusions?.zones ?? [];

  // One site-layer row, reused by every category group (References + Constraints).
  const renderSiteLayerRow = (layer: SiteLayer, testPrefix: string): React.ReactNode => (
    <ContextRow
      key={layer.id}
      label={layer.name}
      detail={`${layer.geometry.type} · ${layer.source.replaceAll('-', ' ')}`}
      color={layer.color}
      selected={selectedIdSet.has(layer.id)}
      onSelect={(additive) => onSelect(layer.id, additive)}
      rowId={layer.id}
      visible={layer.visible}
      onToggleVisibility={onToggleSiteLayer ? () => onToggleSiteLayer(layer.id, !layer.visible) : undefined}
      opacity={siteLayerOpacity[layer.id] ?? 1}
      onOpacityChange={onSetSiteLayerOpacity ? opacity => onSetSiteLayerOpacity(layer.id, opacity) : undefined}
      locked={lockedObjectIds.has(layer.id)}
      onToggleLock={onToggleObjectLock ? () => onToggleObjectLock(layer.id) : undefined}
      onZoom={onZoomTo ? () => onZoomTo({ kind: 'site-layer', id: layer.id }) : undefined}
      testId={`${testPrefix}-${layer.id}`}
    />
  );

  // Category sub-groups within a site-layer section, each with a hide toggle.
  const renderCategoryGroups = (layers: readonly SiteLayer[], testPrefix: string): React.ReactNode =>
    groupLayersByCategory(layers).map(group => {
      const categoryHidden = hiddenCategories.has(group.key);
      return (
        <div key={group.key} data-testid={`studio-layer-category-${group.key}`}>
          <div className="flex min-h-7 items-center gap-1 px-1">
            {onToggleCategory && (
              <button
                type="button"
                onClick={() => onToggleCategory(group.key)}
                aria-pressed={!categoryHidden}
                aria-label={`${categoryHidden ? 'Show' : 'Hide'} ${group.label} category`}
                title={categoryHidden ? 'Show category' : 'Hide category'}
                className="grid h-6 w-6 shrink-0 place-items-center text-text-muted transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                data-testid={`studio-category-toggle-${group.key}`}
              >
                {categoryHidden
                  ? <EyeOff className="h-3.5 w-3.5" aria-hidden />
                  : <Eye className="h-3.5 w-3.5" aria-hidden />}
              </button>
            )}
            <span className={`type-meta flex-1 truncate font-bold ${categoryHidden ? 'opacity-50' : ''}`}>
              {group.label}
              {group.custom && (
                <span className="type-meta ml-1 rounded-sm border border-border px-1 font-bold">custom</span>
              )}
            </span>
            <span className="type-meta shrink-0 tabular-nums">{group.layers.length}</span>
          </div>
          {!categoryHidden && (
            <div>{group.layers.map(layer => renderSiteLayerRow(layer, testPrefix))}</div>
          )}
        </div>
      );
    });
  const hasAnalysis = noiseAvailable || frontlineSet;
  const hasAnyContent = parcels.length > 0
    || referenceLayers.length > 0
    || constraintLayers.length > 0
    || exclusions.length > 0
    || componentGroups.length > 0
    || acousticBarriers.length > 0
    || importedAssignments.length > 0
    || hasAnalysis;

  if (!hasAnyContent) {
    return (
      <div className="p-4 text-center" data-testid="studio-contents-empty">
        <Layers className="mx-auto mb-2 h-5 w-5 text-text-muted" aria-hidden />
        <p className="text-[10px] font-semibold text-text-secondary">No layers yet</p>
        <p className="mt-1 text-[9px] leading-relaxed text-text-muted">
          Confirm a target parcel or import reference geometry to start the layer stack.
        </p>
      </div>
    );
  }

  return (
    <div ref={paneRef} className="min-w-0" data-testid="studio-contents-pane" aria-label="Studio layers">
      {selectedSiteLayers.length >= 2 && (
        <div
          className="sticky top-0 z-10 flex flex-wrap items-center gap-1 border-b border-primary/40 bg-primary/10 px-2 py-1.5"
          data-testid="studio-layer-bulk-bar"
        >
          <span className="mr-1 text-[9px] font-bold uppercase tracking-wider text-primary">
            {selectedSiteLayers.length} layers
          </span>
          {onHideSiteLayers && (
            <button type="button" onClick={() => onHideSiteLayers(selectedSiteLayerIds)} className="border border-border bg-card px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:text-text-primary rounded-sm" data-testid="studio-bulk-hide">Hide</button>
          )}
          {onCopySiteLayers && (
            <button type="button" onClick={() => onCopySiteLayers(selectedSiteLayerIds)} className="border border-border bg-card px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:text-text-primary rounded-sm" data-testid="studio-bulk-copy">Copy</button>
          )}
          {onMergeSiteLayers && (
            <button type="button" onClick={() => onMergeSiteLayers(selectedSiteLayerIds)} disabled={!canMergeSelection} title={canMergeSelection ? 'Merge into one layer' : 'Select layers of the same geometry type to merge'} className="border border-border bg-card px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:text-text-primary disabled:opacity-40 rounded-sm" data-testid="studio-bulk-merge">Merge</button>
          )}
          {onMoveSiteLayers && (
            <select
              value=""
              onChange={(event) => {
                if (event.target.value) onMoveSiteLayers(selectedSiteLayerIds, event.target.value as SiteLayerCategory);
              }}
              aria-label="Move selected layers to category"
              title="Move to category"
              className="border border-border bg-card px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary rounded-sm"
              data-testid="studio-bulk-move"
            >
              <option value="">Move to…</option>
              {CATEGORY_ORDER.map(category => (
                <option key={category} value={category}>{CATEGORY_LABELS[category]}</option>
              ))}
            </select>
          )}
          {onDeleteSiteLayers && (
            <button type="button" onClick={() => onDeleteSiteLayers(selectedSiteLayerIds)} className="border border-danger px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-danger hover:bg-danger/10 rounded-sm" data-testid="studio-bulk-delete">Delete</button>
          )}
        </div>
      )}
      {parcels.length > 0 && (
        <LayerSection label="Parcels" count={parcels.length} testId="studio-layer-group-parcels">
          {parcels.map((parcel, index) => {
            const label = parcel.label?.trim() || `Parcel ${index + 1}`;
            return (
              <ContextRow
                key={parcel.id}
                label={label}
                detail={`${parcel.area.toFixed(1)} ac`}
                current={testFit.parcel?.id === parcel.id}
                selected={selectedIdSet.has(parcel.id)}
                onSelect={(additive) => onSelect(parcel.id, additive)}
                rowId={parcel.id}
                icon={<MapPinned className="h-3.5 w-3.5" aria-hidden />}
                onZoom={onZoomTo
                  ? () => onZoomTo({ kind: 'parcel', id: parcel.id })
                  : undefined}
                testId={`studio-layer-parcel-${parcel.id}`}
              />
            );
          })}
        </LayerSection>
      )}

      {referenceLayers.length > 0 && (
        <LayerSection label="References" count={referenceLayers.length} testId="studio-layer-group-references">
          {renderCategoryGroups(referenceLayers, 'studio-layer-reference')}
        </LayerSection>
      )}

      {importedAssignments.length > 0 && (
        <LayerSection label="Imported Sources" count={importedAssignments.length} testId="studio-layer-group-imported-sources">
          {importedAssignments.map(assignment => (
            <div
              key={assignment.id}
              className="flex min-h-8 items-center gap-1 border-l-2 border-l-transparent px-1 hover:bg-background/70"
              data-testid={`studio-imported-source-${assignment.id}`}
            >
              <div className="min-w-0 flex-1 px-1 py-1">
                <p className="flex items-center gap-1.5 text-[11px] text-text-primary">
                  <span
                    className="shrink-0 border border-success/50 px-1 text-[10px] font-bold uppercase tracking-wider text-success rounded-sm"
                    data-testid={`studio-imported-source-status-${assignment.id}`}
                  >
                    Assigned
                  </span>
                  <span className="truncate" title={assignment.sourceLabel}>{assignment.sourceLabel}</span>
                </p>
                <p className="truncate text-[9px] text-text-muted">
                  {assignment.geometryType} · {STUDIO_ROLE_LABELS[assignment.role]}
                  {assignment.componentId ? ` · ${assignment.componentId}` : ''}
                </p>
              </div>
              {onUnassignSource && (
                <button
                  type="button"
                  onClick={() => onUnassignSource(assignment.id)}
                  title="Unassign this source (removes its geometry; equipment returns to the packer)"
                  aria-label={`Unassign ${assignment.sourceLabel}`}
                  className="shrink-0 border border-border p-1.5 text-text-secondary transition-colors hover:border-danger hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-md"
                  data-testid={`studio-imported-source-unassign-${assignment.id}`}
                >
                  <Unlink className="h-3.5 w-3.5" aria-hidden />
                </button>
              )}
            </div>
          ))}
        </LayerSection>
      )}

      {(constraintLayers.length > 0 || exclusions.length > 0) && (
        <LayerSection
          label="Constraints"
          count={constraintLayers.length + exclusions.length}
          testId="studio-layer-group-constraints"
        >
          {renderCategoryGroups(constraintLayers, 'studio-layer-constraint')}
          {exclusions.map(zone => (
            <ContextRow
              key={zone.id}
              label={zone.label || exclusionKindLabel(zone)}
              detail={`${exclusionKindLabel(zone)} · ${zone.source.replaceAll('-', ' ')}`}
              color="#ef4444"
              icon={<ShieldAlert className="h-3.5 w-3.5 text-danger" aria-hidden />}
              selected={selectedIdSet.has(zone.id)}
              onSelect={(additive) => onSelect(zone.id, additive)}
              rowId={zone.id}
              onZoom={onZoomTo
                ? () => onZoomTo({ kind: 'exclusion', id: zone.id })
                : undefined}
              testId={`studio-layer-exclusion-${zone.id}`}
            />
          ))}
        </LayerSection>
      )}

      {componentGroups.length > 0 && (
        <LayerSection
          label="Generated layout"
          count={testFit.components.length}
          issueCount={testFit.components.filter(componentFlagged).length}
          testId="studio-layer-group-components"
        >
          {componentGroups.map(group => (
            <div key={group.kind}>
              <p className="flex items-baseline gap-1.5 px-2 pb-0.5 pt-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-text-muted">
                {GROUP_LABELS[group.kind]}
                <span className="font-normal tabular-nums text-text-muted/70">{group.components.length}</span>
              </p>
              {group.components.map(component => {
                const rectangle = component.rectangle;
                const selected = selectedIdSet.has(rectangle.id);
                const flagged = componentFlagged(component);
                return (
                  <div
                    key={rectangle.id}
                    data-layer-id={rectangle.id}
                    className={`flex min-h-8 items-center border-l-2 pr-1 transition-colors ${
                      selected
                        ? 'border-l-primary bg-primary/10'
                        : 'border-l-transparent hover:bg-background/70'
                    }`}
                  >
                    <VisibilityButton
                      label={rectangle.label}
                      visible={!hiddenComponentIds.has(rectangle.id)}
                      onToggle={onToggleComponentVisibility
                        ? () => onToggleComponentVisibility(rectangle.id)
                        : undefined}
                    />
                    <button
                      type="button"
                      onClick={(event) => {
                        const additive = event.shiftKey || event.metaKey || event.ctrlKey;
                        if (additive) onSelect(rectangle.id, true);
                        else onSelect(selected ? null : rectangle.id);
                      }}
                      className="flex min-w-0 flex-1 items-center gap-2 px-2 py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
                      data-testid={`studio-contents-row-${rectangle.id}`}
                      aria-pressed={selected}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: COMPONENT_COLORS[rectangle.kind] }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-[10px] text-text-primary" title={rectangle.label}>
                        {rectangle.label}
                      </span>
                      {rectangle.placement === 'pinned' && (
                        <Pin className="h-3 w-3 shrink-0 text-primary" aria-label="Pinned" />
                      )}
                      {flagged && (
                        <AlertTriangle className="h-3 w-3 shrink-0 text-danger" aria-label="Fit issue" />
                      )}
                      <ProvenanceChip
                        basis={rectangle.dimensionBasis}
                        testId={`studio-contents-provenance-${rectangle.id}`}
                      />
                      <span
                        className="shrink-0 text-[9px] tabular-nums text-text-muted"
                        title={`${rectangle.widthM.toFixed(1)} × ${rectangle.heightM.toFixed(1)} m`}
                      >
                        {rectangle.areaAcres.toFixed(1)} ac
                      </span>
                    </button>
                    <LockButton
                      label={rectangle.label}
                      locked={lockedObjectIds.has(rectangle.id)}
                      onToggle={onToggleObjectLock
                        ? () => onToggleObjectLock(rectangle.id)
                        : undefined}
                    />
                    <ZoomButton
                      label={rectangle.label}
                      onClick={onZoomTo
                        ? () => onZoomTo({ kind: 'component', id: rectangle.id })
                        : undefined}
                      testId={`studio-contents-zoom-${rectangle.id}`}
                    />
                  </div>
                );
              })}
            </div>
          ))}
        </LayerSection>
      )}

      {acousticBarriers.length > 0 && (
        <LayerSection label="Acoustic barriers" count={acousticBarriers.length} testId="studio-layer-group-barriers">
          {acousticBarriers.map(barrier => (
            <ContextRow
              key={barrier.id}
              label={barrier.label}
              detail={`${barrier.kind} · ${barrier.heightM.toFixed(1)} m high`}
              color="#22d3ee"
              selected={selectedIdSet.has(barrier.id)}
              onSelect={(additive) => onSelect(barrier.id, additive)}
              rowId={barrier.id}
              onZoom={onZoomTo
                ? () => onZoomTo({ kind: 'barrier', id: barrier.id })
                : undefined}
              testId={`studio-layer-barrier-${barrier.id}`}
            />
          ))}
        </LayerSection>
      )}

      {hasAnalysis && (
        <LayerSection
          label="Analysis"
          count={Number(frontlineSet) + Number(noiseAvailable)}
          testId="studio-layer-group-analysis"
        >
          {frontlineSet && (
            <ContextRow
              label="Frontline alignment"
              detail="Row orientation axis"
              color="#f59e0b"
              onZoom={onZoomTo
                ? () => onZoomTo({ kind: 'frontline', id: 'frontline' })
                : undefined}
              testId="studio-layer-frontline"
            />
          )}
          {noiseAvailable && (
            <ContextRow
              label="Noise contours"
              detail="Grid-screening model"
              visible={noiseVisible}
              onToggleVisibility={onToggleNoise}
              onZoom={onZoomTo
                ? () => onZoomTo({ kind: 'noise', id: 'noise' })
                : undefined}
              icon={<Volume2 className="h-3.5 w-3.5" aria-hidden />}
              testId="studio-layer-noise"
            />
          )}
        </LayerSection>
      )}
    </div>
  );
};
